import { ContactInfo } from '../lib/database.types';

// Helper function to escape vCard text
function escapeVCardValue(value: string): string {
  if (!value) return '';
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

// Helper function to decode escaped text
function decodeEscaped(text: string): string {
  return text
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\:/g, ':')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\\\/g, '\\');
}

// Function to create a vCard string from contact info
export function createVCard(contact: ContactInfo): string {
  if (!contact || !contact.name) {
    throw new Error('Invalid contact information');
  }

  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:3.0', // Using 3.0 for better compatibility
    `FN:${escapeVCardValue(contact.name)}`,
  ];

  // Split name into components (best effort)
  const nameParts = contact.name.split(' ');
  const lastName = nameParts.pop() || '';
  const firstName = nameParts.join(' ');
  lines.push(`N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`);

  // Add email
  if (contact.email) {
    lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(contact.email)}`);
  }

  // Add primary phone
  if (contact.phone) {
    lines.push(`TEL;TYPE=CELL:${escapeVCardValue(contact.phone)}`);
  }

  // Add additional phone numbers
  if (contact.additionalPhones?.length) {
    contact.additionalPhones.forEach((phone) => {
      if (phone) {
        lines.push(`TEL;TYPE=CELL:${escapeVCardValue(phone)}`);
      }
    });
  }

  // Add organization and title
  if (contact.company) {
    lines.push(`ORG:${escapeVCardValue(contact.company)}`);
  }
  if (contact.title) {
    lines.push(`TITLE:${escapeVCardValue(contact.title)}`);
  }

  // Add websites
  if (contact.websites) {
    if (contact.websites.personal) {
      lines.push(`URL;TYPE=HOME:${escapeVCardValue(contact.websites.personal)}`);
    }
    if (contact.websites.work) {
      lines.push(`URL;TYPE=WORK:${escapeVCardValue(contact.websites.work)}`);
    }
    if (contact.websites.blog) {
      lines.push(`URL:${escapeVCardValue(contact.websites.blog)}`);
    }
    if (contact.websites.portfolio) {
      lines.push(`URL:${escapeVCardValue(contact.websites.portfolio)}`);
    }
    if (contact.websites.other?.length) {
      contact.websites.other.forEach(url => {
        if (url) {
          lines.push(`URL:${escapeVCardValue(url)}`);
        }
      });
    }
  }

  // Add social media as URLs
  if (contact.socialMedia) {
    if (contact.socialMedia.linkedin) {
      lines.push(`URL;TYPE=SOCIAL:${escapeVCardValue(contact.socialMedia.linkedin)}`);
    }
    if (contact.socialMedia.facebook) {
      lines.push(`URL;TYPE=SOCIAL:${escapeVCardValue(contact.socialMedia.facebook)}`);
    }
    if (contact.socialMedia.instagram) {
      lines.push(`URL;TYPE=SOCIAL:${escapeVCardValue(contact.socialMedia.instagram)}`);
    }
    if (contact.socialMedia.twitter) {
      lines.push(`URL;TYPE=SOCIAL:${escapeVCardValue(contact.socialMedia.twitter)}`);
    }
  }

  // Add photo if available
  if (contact.photo) {
    // For URLs, use VALUE=URI
    if (contact.photo.startsWith('http')) {
      lines.push(`PHOTO;VALUE=URI:${escapeVCardValue(contact.photo)}`);
    } else if (contact.photo.startsWith('data:')) {
      // For base64 data URLs, extract the image data
      const matches = contact.photo.match(/^data:image\/(\w+);base64,(.+)$/);
      if (matches) {
        const imageType = matches[1].toUpperCase();
        const base64Data = matches[2];

        // vCard 3.0 format for embedded photos
        // Split base64 data into 76-character lines as per spec
        const wrappedData = base64Data.match(/.{1,76}/g)?.join('\r\n ') || base64Data;
        lines.push(`PHOTO;ENCODING=b;TYPE=${imageType}:${wrappedData}`);
      }
    }
  }
  // Add revision timestamp
  lines.push(`REV:${new Date().toISOString()}`);

  lines.push('END:VCARD');

  // Join with CRLF as per vCard spec
  return lines.join('\r\n');
}

// Function to parse vCard data
export function parseVCardData(vcard: string): ContactInfo {
  const data: ContactInfo = {
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    additionalPhones: [],
    socialMedia: {
      facebook: '',
      linkedin: '',
      instagram: '',
      twitter: ''
    },
    websites: {
      personal: '',
      work: '',
      blog: '',
      portfolio: '',
      other: []
    },
    notes: ''
  };

  try {
    // Split into lines and remove empty lines and BEGIN/END
    const lines = vcard
      .split(/\r\n|\r|\n/)
      .filter(line => line.trim() && !line.startsWith('BEGIN:') && !line.startsWith('END:'));

    for (const line of lines) {
      const [property, ...valueParts] = line.split(':');
      const value = valueParts.join(':'); // Rejoin in case value contains colons
      
      if (!value) continue;

      const propName = property.split(';')[0].toUpperCase();
      const decodedValue = decodeEscaped(value.trim());

      switch (propName) {
        case 'FN':
          data.name = decodedValue;
          break;

        case 'N':
          if (!data.name) {
            const [lastName = '', firstName = ''] = decodedValue.split(';');
            data.name = [firstName, lastName].filter(Boolean).join(' ');
          }
          break;

        case 'EMAIL':
          if (!data.email) {
            data.email = decodedValue;
          }
          break;

        case 'TEL':
          if (!data.phone) {
            data.phone = decodedValue;
          } else if (data.additionalPhones) {
            data.additionalPhones.push(decodedValue);
          }
          break;

        case 'ORG':
          data.company = decodedValue.split(';')[0];
          break;

        case 'TITLE':
          data.title = decodedValue;
          break;

        case 'URL':
          if (data.websites && data.websites.other) {
            if (property.includes('TYPE=HOME')) {
              data.websites.personal = decodedValue;
            } else if (property.includes('TYPE=WORK')) {
              data.websites.work = decodedValue;
            } else if (property.includes('TYPE=SOCIAL') && data.socialMedia) {
              // Try to categorize social media URLs
              if (decodedValue.includes('linkedin')) {
                data.socialMedia.linkedin = decodedValue;
              } else if (decodedValue.includes('facebook')) {
                data.socialMedia.facebook = decodedValue;
              } else if (decodedValue.includes('instagram')) {
                data.socialMedia.instagram = decodedValue;
              } else if (decodedValue.includes('twitter')) {
                data.socialMedia.twitter = decodedValue;
              } else {
                data.websites.other.push(decodedValue);
              }
            } else {
              data.websites.other.push(decodedValue);
            }
          }
          break;

        case 'NOTE':
          data.notes = decodedValue;
          break;

        case 'PHOTO':
          // Handle both URI and base64 encoded photos
          if (property.includes('VALUE=URI')) {
            data.photo = decodedValue;
          } else if (property.includes('ENCODING=b') || property.includes('ENCODING=BASE64')) {
            // Extract image type
            const typeMatch = property.match(/TYPE=(\w+)/i);
            const imageType = typeMatch ? typeMatch[1].toLowerCase() : 'jpeg';

            // The value might be split across multiple lines, so we need to handle that
            // For now, just construct the data URL
            const base64Data = decodedValue.replace(/\s+/g, '');
            data.photo = `data:image/${imageType};base64,${base64Data}`;
          }
          break;
      }
    }
  } catch (error) {
    console.error('Error parsing vCard:', error);
  }

  return data;
}